import type {
  CellarOverrides,
  Feature,
  Features,
  SystemAccount,
  SystemCellar,
  SystemSettings,
} from "@corklog/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  ApiError,
  fetchSystemAccounts,
  fetchSystemCellars,
  fetchSystemSettings,
  runWishSearchTickNow,
  saveCellarOverrides,
  saveSystemSettings,
} from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { actionButton, fieldLabel, Select } from "../components/controls.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";
import { useSession } from "../session.js";

type State =
  | { status: "loading" }
  | { status: "ready"; settings: SystemSettings }
  | { status: "failed" };

/** In the order a cellar meets them: a wine, then a dinner, then the whole —
 *  and last *Upptäck*, which is not a question to the model but is switched
 *  the same way, so the administrator decides who is shown it. */
const FEATURES: readonly Feature[] = ["windowHelp", "meal", "quality", "discover"];
const WHO = ["administrator", "others"] as const;

/**
 * *Inställningar* — the administrator's six switches for the three questions
 * to the model. Decided 2026-09-28 in `project-scope.md`.
 *
 * **Not in any cellar**, because a switch here reaches every cellar at once;
 * it hangs off the name in the corner, beside signing out, which is the one
 * piece of chrome that is about the account rather than about a cellar.
 *
 * **A tick saves itself**, as the two on *Gäster* do: six booleans and no form
 * around them, so a *Spara* button would only be one more thing to forget. The
 * tick moves at once and goes back to what the server holds if the write
 * fails — not to the opposite of what was asked, since the write may have
 * landed and only the answer been lost.
 *
 * **The administrator's own column changes their own page at once**: the
 * session is asked again after a save, so switching off *Till maten* for
 * yourself takes the link out of the row without a reload. Everybody else
 * finds out on their next reload, and their routes refuse in the meantime.
 */
export function Settings() {
  const { t } = useTranslation();
  const session = useSession();
  const [state, setState] = useState<State>({ status: "loading" });
  const [saveFailed, setSaveFailed] = useState(false);

  const load = useCallback(() => {
    void fetchSystemSettings()
      .then((settings) => setState({ status: "ready", settings }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  // The link is drawn only for the administrator, and the route answers 404
  // to anybody else; this is the sentence for an address typed anyway.
  if (session.status === "signedIn" && !session.user.isAdministrator) {
    return (
      <section>
        <CellarHeading cellarId={null} current="settings" />
        <p className="text-muted mt-6 max-w-prose text-sm">{t("settings.notYours")}</p>
      </section>
    );
  }

  const change = (
    settings: SystemSettings,
    who: (typeof WHO)[number],
    feature: Feature,
    on: boolean,
  ) => {
    const next: SystemSettings = {
      ...settings,
      [who]: { ...settings[who], [feature]: on },
    };
    setState({ status: "ready", settings: next });
    setSaveFailed(false);
    void saveSystemSettings(next)
      .then((saved) => {
        setState({ status: "ready", settings: saved });
        if (who === "administrator") void session.refresh();
      })
      .catch(() => {
        setSaveFailed(true);
        load();
      });
  };

  // The fourth question has one switch, the administrator's: nobody else may
  // run it yet. Saved the same way as the six. See `project-scope.md`.
  const changeAreaReports = (settings: SystemSettings, on: boolean) =>
    changeTop(settings, { areaReports: on });

  // The weekly wish search is allowed here, for every account at once, and
  // each account then chooses on its own wish list. Saved the same way.
  const changeWishSearch = (settings: SystemSettings, on: boolean) =>
    changeTop(settings, { wishSearch: on });

  // One tick of the weekly search, started by hand. Takes most of a minute, so
  // the button says so while it waits; the answer says whether another press
  // has work.
  const [ticking, setTicking] = useState(false);
  const [tick, setTick] = useState<
    null | "failed" | "switchedOff" | { searched: number; failed: number; more: boolean }
  >(null);
  const runTick = () => {
    setTicking(true);
    setTick(null);
    void runWishSearchTickNow()
      .then(setTick)
      .catch((error: unknown) =>
        setTick(
          error instanceof ApiError && error.status === 409 ? "switchedOff" : "failed",
        ),
      )
      .finally(() => setTicking(false));
  };

  const changeTop = (settings: SystemSettings, change: Partial<SystemSettings>) => {
    const next: SystemSettings = { ...settings, ...change };
    setState({ status: "ready", settings: next });
    setSaveFailed(false);
    void saveSystemSettings(next)
      .then((saved) => setState({ status: "ready", settings: saved }))
      .catch(() => {
        setSaveFailed(true);
        load();
      });
  };

  return (
    <section>
      {/* The heading every cellar's page wears, since 2026-10-08, on the
          owner's asking: the row and *Mer* of the cellar last opened. It
          replaced *Tillbaka*, which led to whichever page the cog was pressed
          on — a way back, where the menu is a way on to anywhere. */}
      <CellarHeading cellarId={null} current="settings" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("settings.intro")}</p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("settings.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        // A table, because it is one: three questions down, two kinds of
        // reader across, and every cell the same kind of answer. The header
        // cells are what a screen reader names each tick by.
        <table className="mt-6 w-full max-w-prose text-sm">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="py-2 text-left font-normal">
                <span className="sr-only">{t("settings.question")}</span>
              </th>
              {WHO.map((who) => (
                <th
                  key={who}
                  scope="col"
                  className="text-muted w-28 py-2 text-center font-normal"
                >
                  {t(`settings.who.${who}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((feature) => (
              <tr key={feature} className="border-line border-b">
                <th scope="row" className="py-3 pr-4 text-left font-normal">
                  {t(`settings.feature.${feature}`)}
                  <span className="text-muted mt-0.5 block text-xs">
                    {t(`settings.explain.${feature}`)}
                  </span>
                </th>
                {WHO.map((who) => (
                  <td key={who} className="py-3 text-center">
                    <input
                      type="checkbox"
                      checked={state.settings[who][feature]}
                      onChange={(event) =>
                        change(state.settings, who, feature, event.target.checked)
                      }
                      aria-label={t(`settings.tick.${who}`, {
                        feature: t(`settings.feature.${feature}`),
                      })}
                      className="accent-accent"
                    />
                  </td>
                ))}
              </tr>
            ))}
            {/* The fourth question, the administrator's alone. The other cell
                says so rather than drawing a tick that decides nothing. */}
            <tr className="border-line border-b">
              <th scope="row" className="py-3 pr-4 text-left font-normal">
                {t("settings.feature.areaReports")}
                <span className="text-muted mt-0.5 block text-xs">
                  {t("settings.explain.areaReports")}{" "}
                  <Link
                    to="/installningar/omraden"
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("settings.areaReportsLink")}
                  </Link>
                </span>
              </th>
              <td className="py-3 text-center">
                <input
                  type="checkbox"
                  checked={state.settings.areaReports}
                  onChange={(event) =>
                    changeAreaReports(state.settings, event.target.checked)
                  }
                  aria-label={t("settings.tick.administrator", {
                    feature: t("settings.feature.areaReports"),
                  })}
                  className="accent-accent"
                />
              </td>
              <td className="text-muted py-3 text-center text-xs">
                {t("settings.onlyYou")}
              </td>
            </tr>
            {/* Allowed for everybody or nobody: each account's own choice is
                made on its wish list, so there is one tick and not two. */}
            <tr className="border-line border-b">
              <th scope="row" className="py-3 pr-4 text-left font-normal">
                {t("settings.feature.wishSearch")}
                <span className="text-muted mt-0.5 block text-xs">
                  {t("settings.explain.wishSearch")}
                </span>
                <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <button
                    type="button"
                    onClick={runTick}
                    disabled={ticking}
                    className={actionButton}
                  >
                    {ticking
                      ? t("settings.wishSearchRunning")
                      : t("settings.wishSearchRun")}
                  </button>
                  {tick === "failed" && (
                    <span role="alert" className="text-past text-xs">
                      {t("settings.wishSearchRunFailed")}
                    </span>
                  )}
                  {tick === "switchedOff" && (
                    <span role="alert" className="text-past text-xs">
                      {t("settings.wishSearchSwitchedOff")}
                    </span>
                  )}
                  {tick !== null && tick !== "failed" && tick !== "switchedOff" && (
                    <span role="status" className="text-muted text-xs">
                      {t("settings.wishSearchRan", { count: tick.searched })}
                      {tick.failed > 0 &&
                        ` ${t("settings.wishSearchRanFailed", { count: tick.failed })}`}
                      {tick.more && ` ${t("settings.wishSearchRanMore")}`}
                    </span>
                  )}
                </span>
              </th>
              <td className="text-muted py-3 text-center text-xs" />
              <td className="py-3 text-center">
                <input
                  type="checkbox"
                  checked={state.settings.wishSearch}
                  onChange={(event) =>
                    changeWishSearch(state.settings, event.target.checked)
                  }
                  aria-label={t("settings.tick.others", {
                    feature: t("settings.feature.wishSearch"),
                  })}
                  className="accent-accent"
                />
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {saveFailed && (
        <p role="alert" className="text-past mt-4 text-sm">
          {t("settings.saveFailed")}
        </p>
      )}

      <PerCellar others={state.status === "ready" ? state.settings.others : null} />
      <Accounts />
    </section>
  );
}

type Choice = "follow" | "on" | "off";

function choiceOf(value: boolean | null): Choice {
  return value === null ? "follow" : value ? "on" : "off";
}

function valueOf(choice: string): boolean | null {
  return choice === "on" ? true : choice === "off" ? false : null;
}

type CellarsState =
  | { status: "loading" }
  | { status: "ready"; cellars: SystemCellar[] }
  | { status: "failed" };

/**
 * Each cellar's own answer to the three questions, replacing *Alla andra*
 * there. Added later the same day; see `project-scope.md`.
 *
 * **A block per cellar rather than a fourth column in the table above**: every
 * cellar in the system, with its owner's name under it and three choices
 * beside it, does not fit across a phone as a row, and wrapping a table row is
 * how a table stops being read as one.
 *
 * **The first choice says what it means today** — *Som alla andra (på)* — so
 * nobody has to look up at the table to know what a cellar left alone is
 * getting. A cellar answered differently is drawn in brass, as a narrowed
 * filter is: it is the exception, and the eye should find it in a long list.
 *
 * Saved on change, as the ticks above are, and put back to what the server
 * holds if the write fails.
 */
function PerCellar({ others }: { others: Features | null }) {
  const { t } = useTranslation();
  const [state, setState] = useState<CellarsState>({ status: "loading" });
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(() => {
    void fetchSystemCellars()
      .then(({ cellars }) => setState({ status: "ready", cellars }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  const change = (cellar: SystemCellar, feature: Feature, choice: string) => {
    const overrides: CellarOverrides = {
      ...cellar.overrides,
      [feature]: valueOf(choice),
    };
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            cellars: current.cellars.map((row) =>
              row.id === cellar.id ? { ...row, overrides } : row,
            ),
          }
        : current,
    );
    setFailed(null);
    void saveCellarOverrides(cellar.id, overrides).catch(() => {
      setFailed(cellar.name);
      load();
    });
  };

  return (
    <section className="mt-10">
      <h2 className="font-display text-xl">{t("settings.perCellar.title")}</h2>
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t("settings.perCellar.intro")}
      </p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("settings.perCellar.loadFailed")}
        </p>
      )}
      {failed !== null && (
        <p role="alert" className="text-past mt-4 text-sm">
          {t("settings.perCellar.saveFailed", { name: failed })}
        </p>
      )}

      {state.status === "ready" && (
        <ul className="mt-4">
          {state.cellars.map((cellar) => (
            <li key={cellar.id} className="border-line border-b py-4">
              <p>{cellar.name}</p>
              <p className="text-muted text-xs">{cellar.ownerName}</p>
              <div className="mt-3 flex flex-wrap gap-4">
                {FEATURES.map((feature) => {
                  const id = `override-${cellar.id}-${feature}`;
                  const current = choiceOf(cellar.overrides[feature]);
                  return (
                    <div key={feature} className="w-52">
                      <label className={fieldLabel} htmlFor={id}>
                        {t(`settings.feature.${feature}`)}
                      </label>
                      <div className="mt-1">
                        <Select
                          id={id}
                          value={current}
                          active={current !== "follow"}
                          onChange={(next) => change(cellar, feature, next)}
                        >
                          <option value="follow">
                            {others === null
                              ? t("settings.perCellar.follow")
                              : t("settings.perCellar.followNow", {
                                  state: t(
                                    others[feature]
                                      ? "settings.perCellar.on"
                                      : "settings.perCellar.off",
                                  ).toLocaleLowerCase("sv"),
                                })}
                          </option>
                          <option value="on">{t("settings.perCellar.on")}</option>
                          <option value="off">{t("settings.perCellar.off")}</option>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type AccountsState =
  | { status: "loading" }
  | { status: "ready"; accounts: SystemAccount[] }
  | { status: "failed" };

/** A day and a time, Swedish, for *senast inloggad* — the day alone would
 *  not tell this morning from last night. */
function when(iso: string): string {
  return new Date(iso).toLocaleString(locale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Every account, and when each last signed in — added 2026-09-29, asked for
 * by the owner; see `project-scope.md`.
 *
 * **Last signed in and last seen are both said**, because a session lasts
 * weeks: somebody who signed in a month ago and used the app this morning
 * would otherwise read as absent. *Senast aktiv* is to a quarter of an hour,
 * which is what `lastSeenAt` is written at. Read-only: what the
 * administrator does about an account is still a written procedure.
 */
function Accounts() {
  const { t } = useTranslation();
  const [state, setState] = useState<AccountsState>({ status: "loading" });

  const load = useCallback(() => {
    void fetchSystemAccounts()
      .then(({ accounts }) => setState({ status: "ready", accounts }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useReload(load);

  return (
    <section className="mt-10">
      <h2 className="font-display text-xl">{t("settings.accounts.title")}</h2>
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t("settings.accounts.intro")}
      </p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("settings.accounts.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <ul className="mt-4">
          {state.accounts.map((account) => (
            <li
              key={account.id}
              className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b py-3"
            >
              <div className="min-w-0">
                <p>
                  {account.name}
                  {account.isAdministrator && (
                    <span className="text-muted ml-2 text-xs">
                      {t("settings.accounts.administrator")}
                    </span>
                  )}
                </p>
                <p className="text-muted text-xs break-all">{account.email}</p>
              </div>
              <div className="text-sm sm:text-right">
                <p>
                  {account.lastSignedInAt === null
                    ? t("settings.accounts.neverSignedIn")
                    : t("settings.accounts.lastSignedIn", {
                        date: when(account.lastSignedInAt),
                      })}
                </p>
                {account.lastSeenAt !== null && (
                  <p className="text-muted text-xs">
                    {t("settings.accounts.lastSeen", { date: when(account.lastSeenAt) })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
